import { useEffect, useRef } from 'react';
import '../App.css';
import { drawCircle, drawArrowByAngle } from '../utils/RouteCanvasUtils';
import { StyleConstants } from '../Styles';

const CANVAS_WIDTH = 1410;
const CANVAS_HEIGHT = 520;

const RouteCanvas = (props) => {
    const { left, top, routeCode, stations, routeStations, carPoss, carFaults } = props;

    const carCanvasRef = useRef(null);
    const carCodeBgCanvasRef = useRef(null);

    useEffect(() => {
        if (carPoss) {
            const carCanvas = carCanvasRef.current;
            carCanvas.width = CANVAS_WIDTH;
            carCanvas.height = CANVAS_HEIGHT;
            const carCanvasContext = carCanvas.getContext('2d');
            carCanvasContext.globalCompositeOperation = 'destination-over';
            carCanvasContext.font = 'bold 12px Arial';

            const carCodeBgCanvas = carCodeBgCanvasRef.current;
            carCodeBgCanvas.width = CANVAS_WIDTH;
            carCodeBgCanvas.height = CANVAS_HEIGHT;
            const carCodeBgCanvasContext = carCodeBgCanvas.getContext('2d');
            carCodeBgCanvasContext.globalCompositeOperation = 'destination-over';
            carCodeBgCanvasContext.font = 'bold 12px Arial';

            const runCarMap = [];
            for (let i = 0; i < carPoss.length; i += 1) {
                if (carPoss[i].runCode) {
                    if (!Array.isArray(runCarMap[carPoss[i].runCode])) {
                        runCarMap[carPoss[i].runCode] = [];
                        runCarMap[carPoss[i].runCode][0] = carPoss[i].carCode;
                    } else {
                        runCarMap[carPoss[i].runCode][1] = carPoss[i].carCode;
                    }
                }
            }

            const routeStationMap = [];
            const routeDestStationMap = [];
            for (let i = 0; i < routeStations.length; i += 1) {
                let key = routeStations[i].routeCode + "|" + routeStations[i].stationCode;
                routeStationMap[key] = routeStations[i];

                key = routeStations[i].routeCode + "|" + routeStations[i].destStationCode + "|" + routeStations[i].stationCode;
                routeDestStationMap[key] = routeStations[i];
            }

            for (let i = 0; i < carPoss.length; i += 1) {
                let minFaultTypeLevel = 3;
                for (let j = 0; j < carFaults.length; j += 1) {
                    if (carPoss[i].carCode === carFaults[j].carCode) {
                        let vvvfFaultLevel = carFaults[j].vvvfFaultLevel === 0 ? 3 : carFaults[j].vvvfFaultLevel;
                        let sivFaultLevel = carFaults[j].sivFaultLevel === 0 ? 3 : carFaults[j].sivFaultLevel;
                        let brakeFaultLevel = carFaults[j].brakeFaultLevel === 0 ? 3 : carFaults[j].brakeFaultLevel;
                        let doorFaultLevel = carFaults[j].doorFaultLevel === 0 ? 3 : carFaults[j].doorFaultLevel;
                        let vacFaultLevel = carFaults[j].vacFaultLevel === 0 ? 3 : carFaults[j].vacFaultLevel;

                        minFaultTypeLevel = Math.min(
                            vvvfFaultLevel,
                            sivFaultLevel,
                            brakeFaultLevel,
                            doorFaultLevel,
                            vacFaultLevel
                        );

                        if (minFaultTypeLevel === 3) {
                            minFaultTypeLevel = 0;
                        }
                        break;
                    }
                }

                let routeDirectionColor = null;
                let routeAngle = null;
                let posX = null;
                let posY = null;

                if (routeCode === 'map') {
                    for (let j = 0; j < stations.length; j += 1) {
                        if (carPoss[i].stationCode === stations[j].stationCode) {
                            posX = stations[j].posX;
                            posY = stations[j].posY;
                            break;
                        }
                    }
                } else {
                    const key = carPoss[i].routeCode + "|" + carPoss[i].stationCode;
                    const routeStation = routeStationMap[key];
                    if (routeStation) {
                        posX = routeStation.posX;
                        posY = routeStation.posY;
                    }
                }

                const key = carPoss[i].routeCode + "|" + carPoss[i].destStationCode + "|" + carPoss[i].stationCode;
                const routeStation = routeDestStationMap[key];
                if (routeStation) {
                    if (routeCode === 'map') {
                        routeAngle = routeStation.mapAngle;
                    } else {
                        routeAngle = routeStation.routeAngle;
                    }

                    switch (routeAngle) {
                        case 0:
                        case 45:
                        case 90:
                        case 135:
                            routeDirectionColor = StyleConstants.routeUpColor;
                            break;
                        case 180:
                        case 225:
                        case 270:
                        case 315:
                            routeDirectionColor = StyleConstants.routeDownColor;
                            break;
                    }
                }
    
                if ((posX !== null) && (posY !== null)) {
                    if (routeAngle === null) {
                        routeDirectionColor = StyleConstants.routeUpColor;
                    }
                    
                    drawCircle(carCanvasContext, posX, posY, routeDirectionColor);

                    if (routeAngle !== null) {
                        drawArrowByAngle(carCanvasContext, posX, posY, routeAngle, routeDirectionColor);
                    } else {
                        routeAngle = '';
                    }

                    let textColor = null;
                    switch (minFaultTypeLevel) {
                    case 2:
                        textColor = StyleConstants.faultLevelTwo;
                        break;
                    case 1:
                        textColor = StyleConstants.faultLevelOne;
                        break;
                    case 0:
                    default:
                        textColor = StyleConstants.normalColor;
                    }

                    let textPosX = posX;
                    let textPosY = posY;

                    let carIndex = 0;
                    if (carPoss[i].runCode) {
                        carIndex = runCarMap[carPoss[i].runCode].indexOf(carPoss[i].carCode);
                    }
    
                    switch (routeAngle) {
                        case 0:
                            textPosX += 10;
                            if (carIndex === 0) {
                                textPosY -= 20;
                            } else {
                                textPosY -= 8;
                            }
                            break;
                        case 45:
                            textPosX += 12;
                            if (carIndex === 0) {
                                textPosY += 8;
                            } else {
                                textPosY += 20;
                            }
                            break;
                        case 90:
                            textPosX += 6;
                            if (carIndex === 0) {
                                textPosY += 22;
                            } else {
                                textPosY += 34;
                            }
                            break;
                        case 135:
                            textPosX += 25;
                            if (carIndex === 0) {
                                textPosY += 10;
                            } else {
                                textPosY += 22;
                            }
                            break;
                        case 180:
                            textPosX += 10;
                            if (carIndex === 0) {
                                textPosY += 18;
                            } else {
                                textPosY += 30;
                            }
                            break;
                        case 225:
                            if (carIndex === 0) {
                                textPosY += 22;
                            } else {
                                textPosY += 34;
                            }
                            break;
                        case 270:
                            textPosX -= 32;
                            if (carIndex === 0) {
                                textPosY += 22;
                            } else {
                                textPosY += 34;
                            }
                            break;
                        case 315:
                            textPosX -= 36;
                            if (carIndex === 0) {
                                textPosY += 12;
                            } else {
                                textPosY += 24;
                            }
                            break;
                        default:
                            textPosX += 6;
                            if (carIndex === 0) {
                                textPosY += 22;
                            } else {
                                textPosY += 34;
                            }
                    }

                    carCodeBgCanvasContext.fillStyle = 'black';
                    carCodeBgCanvasContext.fillRect(textPosX, textPosY - 10, 26, 11);
    
                    carCanvasContext.fillStyle = textColor;
                    carCanvasContext.fillText(carPoss[i].carCode, textPosX, textPosY);
                }
            }
        }
    }, [carPoss]);

    return (
        <>
            <canvas ref={carCanvasRef} style={{ position: 'absolute', width: '1410px', height: '520px', left: left, top: top, zIndex: 30 }}></canvas>
            <canvas ref={carCodeBgCanvasRef} style={{ position: 'absolute', width: '1410px', height: '520px', left: left, top: top, zIndex: 20 }}></canvas>
            <img
                src={ require('../assets/images/route/route_' + routeCode + '.png') }
                style={{
                    position: 'absolute',
                    width: '1410px',
                    height: '520px',
                    left: left,
                    top: top,
                    zIndex: 10
                }}
                alt=""
            />
        </>
    );
};

export default RouteCanvas;